import {makeLogger} from '../../../../logger/logger';
import {Service} from 'typedi';
import {StrategyBuilder} from '../../StrategyBuilder';
import {QueryPrecachingStrategyService} from '../../QueryPrecachingStrategyService';
import type {PrechachingData} from '../../types/precaching-data.type';
import {STRATEGY_SERVICE} from '../../utils/variables';
import {compress, decompress} from './compression';

const {info,err} = makeLogger({
  pathToCurrentFile: 'components/strategies/services/precaching-service/precaching-precache.service.ts'
});


//For compression
//const compressedReadableStream = inputReadableStream.pipeThrough(new CompressionStream('gzip'));


@Service({id: 'precache' + STRATEGY_SERVICE})
export class PrecachingPrecacheStrategyServiceBuilder extends StrategyBuilder<QueryPrecachingStrategyService> {

  static _cacheInstance:QueryPrecachingStrategyService | undefined = undefined;

  build(details: PrechachingData): QueryPrecachingStrategyService {
      if(PrecachingPrecacheStrategyServiceBuilder._cacheInstance)
        return PrecachingPrecacheStrategyServiceBuilder._cacheInstance;
      PrecachingPrecacheStrategyServiceBuilder._cacheInstance = new PrecachingPrecacheStrategy(details);
      return PrecachingPrecacheStrategyServiceBuilder._cacheInstance;
  }
}

export class PrecachingPrecacheStrategy extends QueryPrecachingStrategyService {
  details: PrechachingData;
  keys: string[] = [];
  store: Record<string, string> = {};

  constructor(details: PrechachingData) {
      super();
      this.details = details;
  }

  private isReady:boolean = false;
  public prepareReinitialization(){
      this.isReady = false;
      this.store = {};
      this.keys = [];
  }
  public async initialize(){
      if(this.isReady){
        return;
      }
      this.isReady = true;
      const selector = this.details?.params?.readFrom ?? 'no-such-tag';
      const tags = document.querySelectorAll(selector) as unknown as Array<{
        dataRaw:Record<string,string>|null,
        getAttribute: (attributeName: string) => string|undefined,
      }>;

      await Promise.all(Array.prototype.map.call(tags,async tag=> {

          if (!tag) {
            return;
          }
          this.scanForDataRaw(tag);
          this.scanForDataPlain(tag);
          await this.scanForDataGzipped(tag);
      }));
      this.keys = this.store ? Object.keys(this.store) : [];
      const keyCount = this.keys.length;
      info(`Total key count ${keyCount}`, this.keys);
  }


  private scanForDataRaw(tag){
  //dataRaw is runtime object {[key:string]:Json<CoseResponse>} assigned to tags attribute
      if(tag.dataRaw){
        try{
          this.store = {...this.store, ...tag.dataRaw};
          delete tag.dataRaw;
        }catch(ignored){}
      }
  }

  private scanForDataPlain(tag){
      //dataPlain is stringified object {[key:string]:CoseResponse} assigned to tags attribute
      const dataPlain = (tag).getAttribute('data-plain');
      if(dataPlain) {

        try {
          const loaded = JSON.parse(dataPlain);
          this.store = {...this.store, ...loaded};
          // const keyCount = Object.keys(loaded).length;
          // info(`Adding ${ keyCount } items as Plain`, loaded);
        } catch (e) {
          err('Error while parsing data (dataPlain) into cache', e);
        }
      }

  }

  private async scanForDataGzipped(tag) {
  //dataGzipped is stringified object {[key:string]:CoseResponse} compressed with base64+gzip
      const gzipped = tag.getAttribute('data-gzipped');
      if (gzipped) {


        try {
          const data = await decompress(gzipped);
          const loaded = JSON.parse(data);
          this.store = {...this.store, ...loaded};
          // const keyCount = Object.keys(loaded).length;
          // info(`Adding ${ keyCount } items as Gzipped`, loaded);
        } catch (e) {
          err('Error while parsing data (dataGzipped) into cache', e);
        }

      }
  }



  hasInCache(cacheKey: string): boolean {
      return this.keys.includes(cacheKey);
  }

  getFromCache<T>(cacheKey: string):T|undefined {
      try{
        const item = this.store[cacheKey] as T;
        if(item){
          info('Cache hit', cacheKey);
          return item;
        } else {
          info('Cache miss', cacheKey);
        }
      }catch(e){
        err('Error while getting data from cache', e);
        return undefined;
      }
  }
  async getFromCacheWithCallback<T>(cacheKey: string, callback:()=>Promise<T>):Promise<T>|undefined {
      const item = this.store[cacheKey];

      try{
        if(item) {
          return JSON.parse(item) as T;
        }
      }catch(ignored){}


      const r = await callback();
      const cachedValue = JSON.stringify(r);
      this.store[cacheKey] = cachedValue;
      await this.showCacheEntry(cacheKey, cachedValue);
      return r;

  }

  private async showCacheEntry(cacheKey: string, cacheValue: string): Promise<void> {
      const cacheMarkup = document.createElement('div');
      const cacheMarkupPlain = document.createElement('div');

      cacheMarkup.setAttribute('data-gzipped', await compress(JSON.stringify({[cacheKey]:cacheValue})));
      cacheMarkupPlain.setAttribute('data-plain', JSON.stringify({[cacheKey]:cacheValue}));
      const markupCompressed = cacheMarkup.outerHTML;
      const markupPlain = cacheMarkupPlain.outerHTML;
      cacheMarkup.remove();
      cacheMarkupPlain.remove();
      info('Preloaded cache item not found, fetched real data', {
        cacheKey: cacheKey,
        cacheValue: cacheValue,
        markupCompressed,
        markupPlain
      });

  }
}

/*

Sample snippet for Cose

 */
